import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

export default function TrainingPage({ user }) {
  const [files, setFiles] = useState([]);
  const [globalFiles, setGlobalFiles] = useState([]);
  const [skills, setSkills] = useState('');
  const [savedSkills, setSavedSkills] = useState('');
  const [orgDescription, setOrgDescription] = useState('');
  const [savedOrgDesc, setSavedOrgDesc] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  // Admin: view employee files
  const [allUsers, setAllUsers] = useState([]);
  const [viewUserId, setViewUserId] = useState('');
  const [viewUserData, setViewUserData] = useState(null);

  const isAdmin = user.role === 'admin';

  const load = useCallback(async () => {
    try {
      const [myFiles, gFiles, sk] = await Promise.all([
        api.getMyFiles(),
        api.getGlobalFiles(),
        api.getSkills(),
      ]);
      setFiles(myFiles);
      setGlobalFiles(gFiles);
      setSkills(sk.skills_text || '');
      setSavedSkills(sk.skills_text || '');
    } catch {}

    if (isAdmin) {
      try {
        const [usrs, orgDesc] = await Promise.all([
          api.getUsers(),
          api.getOrgDescription(),
        ]);
        setAllUsers(usrs.filter(u => u.role !== 'admin'));
        setOrgDescription(orgDesc.org_description || '');
        setSavedOrgDesc(orgDesc.org_description || '');
      } catch {}
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  async function loadViewUser(userId) {
    setViewUserId(userId);
    if (!userId) { setViewUserData(null); return; }
    try {
      const data = await api.getUserFiles(userId);
      setViewUserData(data);
    } catch { setViewUserData(null); }
  }

  async function handleUpload(e, isGlobal) {
    const fileList = e.target.files;
    if (!fileList || !fileList.length) return;
    setUploading(true); setMsg('');
    try {
      await api.uploadFiles(Array.from(fileList), isGlobal);
      setMsg('Файлы загружены');
      load();
    } catch (err) { setMsg('Ошибка: ' + err.message); }
    setUploading(false);
    e.target.value = '';
  }

  async function handleDeleteFile(id) {
    try { await api.deleteFile(id); load(); } catch {}
  }

  async function handleSaveSkills() {
    setSaving(true); setMsg('');
    try {
      await api.saveSkills(skills);
      setSavedSkills(skills);
      setMsg('Навыки сохранены');
    } catch (err) { setMsg('Ошибка: ' + err.message); }
    setSaving(false);
  }

  async function handleSaveOrgDesc() {
    setSaving(true); setMsg('');
    try {
      await api.saveOrgDescription(orgDescription);
      setSavedOrgDesc(orgDescription);
      setMsg('Описание организации сохранено');
    } catch (err) { setMsg('Ошибка: ' + err.message); }
    setSaving(false);
  }

  return (
    <div className="admin-page">
      <h2 style={{ marginBottom: 8 }}>Обучение ИИ</h2>
      <p style={{ color: 'var(--text2)', fontSize: '0.85rem', marginBottom: 24 }}>
        Загружайте файлы и описывайте навыки — ИИ будет учитывать это в каждом разговоре.
      </p>

      {msg && <div style={{ padding: '8px 16px', background: msg.startsWith('Ош') ? 'rgba(231,76,60,0.2)' : 'rgba(0,184,148,0.2)', color: msg.startsWith('Ош') ? 'var(--danger)' : 'var(--success)', borderRadius: 6, marginBottom: 16, fontSize: '0.85rem' }}>{msg}</div>}

      {/* ====== ГРУППА 1: ПРО ОРГАНИЗАЦИЮ (только админ) ====== */}
      {isAdmin && (
        <div style={{ marginBottom: 40, padding: 20, background: 'var(--bg2)', border: '2px solid var(--accent)', borderRadius: 'var(--radius)' }}>
          <h3 style={{ color: 'var(--accent)', marginBottom: 4 }}>Про организацию</h3>
          <p style={{ color: 'var(--text2)', fontSize: '0.8rem', marginBottom: 16 }}>
            Общая информация о компании. Доступна ИИ для ВСЕХ сотрудников.
          </p>

          {/* Org Description */}
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ marginBottom: 8, fontSize: '0.9rem' }}>Описание организации</h4>
            <textarea
              value={orgDescription}
              onChange={e => setOrgDescription(e.target.value)}
              placeholder="Опишите деятельность компании, ценности, услуги, регламенты..."
              style={{
                width: '100%', minHeight: 140, padding: 14, background: 'var(--bg3)', border: '1px solid var(--border)',
                borderRadius: 6, color: 'var(--text)', fontSize: '0.9rem', resize: 'vertical', outline: 'none', fontFamily: 'inherit', lineHeight: 1.6,
              }}
            />
            <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center' }}>
              <button className="btn-add" onClick={handleSaveOrgDesc} disabled={saving || orgDescription === savedOrgDesc}>
                {saving ? 'Сохраняю...' : 'Сохранить описание'}
              </button>
              {orgDescription !== savedOrgDesc && <span style={{ color: 'var(--accent2)', fontSize: '0.8rem' }}>Есть несохранённые изменения</span>}
            </div>
          </div>

          {/* Global Files */}
          <div>
            <h4 style={{ marginBottom: 8, fontSize: '0.9rem' }}>Файлы организации</h4>
            <label className="btn-add" style={{ display: 'inline-block', cursor: 'pointer' }}>
              {uploading ? 'Загрузка...' : '+ Загрузить общие файлы'}
              <input type="file" multiple accept=".txt,.md,.csv,.json,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,image/*" style={{ display: 'none' }} onChange={e => handleUpload(e, true)} />
            </label>
            {globalFiles.length > 0 && (
              <table className="users-table" style={{ marginTop: 12 }}>
                <thead><tr><th>Файл</th><th>Загружен</th><th></th></tr></thead>
                <tbody>
                  {globalFiles.map(f => (
                    <tr key={f.id}>
                      <td style={{ fontWeight: 600 }}>{f.original_name}</td>
                      <td style={{ color: 'var(--text2)', fontSize: '0.85rem' }}>{f.created_at?.slice(0, 16).replace('T', ' ')}</td>
                      <td><button className="btn-sm" onClick={() => handleDeleteFile(f.id)}>Удалить</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ====== ГРУППА 2: ЛИЧНОЕ ОБУЧЕНИЕ (все) ====== */}
      <div style={{ marginBottom: 40, padding: 20, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
        <h3 style={{ marginBottom: 4 }}>Личное обучение ИИ</h3>
        <p style={{ color: 'var(--text2)', fontSize: '0.8rem', marginBottom: 16 }}>
          Ваши файлы и навыки. ИИ использует их только при общении с вами.
        </p>

        {/* Personal Files */}
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ marginBottom: 8, fontSize: '0.9rem' }}>Мои файлы</h4>
          <label className="btn-add" style={{ display: 'inline-block', cursor: 'pointer' }}>
            {uploading ? 'Загрузка...' : '+ Загрузить файлы'}
            <input type="file" multiple accept=".txt,.md,.csv,.json,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,image/*" style={{ display: 'none' }} onChange={e => handleUpload(e, false)} />
          </label>
          {files.filter(f => !f.is_global).length > 0 && (
            <table className="users-table" style={{ marginTop: 12 }}>
              <thead><tr><th>Файл</th><th>Загружен</th><th></th></tr></thead>
              <tbody>
                {files.filter(f => !f.is_global).map(f => (
                  <tr key={f.id}>
                    <td style={{ fontWeight: 600 }}>{f.original_name}</td>
                    <td style={{ color: 'var(--text2)', fontSize: '0.85rem' }}>{f.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td><button className="btn-sm" onClick={() => handleDeleteFile(f.id)}>Удалить</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Skills */}
        <div>
          <h4 style={{ marginBottom: 8, fontSize: '0.9rem' }}>Навыки и специализация</h4>
          <textarea
            value={skills}
            onChange={e => setSkills(e.target.value)}
            placeholder="Опишите свои навыки, специализацию, стиль работы..."
            style={{
              width: '100%', minHeight: 140, padding: 14, background: 'var(--bg3)', border: '1px solid var(--border)',
              borderRadius: 6, color: 'var(--text)', fontSize: '0.9rem', resize: 'vertical', outline: 'none', fontFamily: 'inherit', lineHeight: 1.6,
            }}
          />
          <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center' }}>
            <button className="btn-add" onClick={handleSaveSkills} disabled={saving || skills === savedSkills}>
              {saving ? 'Сохраняю...' : 'Сохранить навыки'}
            </button>
            {skills !== savedSkills && <span style={{ color: 'var(--accent2)', fontSize: '0.8rem' }}>Есть несохранённые изменения</span>}
          </div>
        </div>
      </div>

      {/* Non-admin sees global files as read-only */}
      {!isAdmin && globalFiles.length > 0 && (
        <div style={{ marginBottom: 32, padding: 20, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
          <h3 style={{ marginBottom: 12, color: 'var(--text2)' }}>Файлы организации (от администратора)</h3>
          <table className="users-table">
            <thead><tr><th>Файл</th><th>Загружен</th></tr></thead>
            <tbody>
              {globalFiles.map(f => (
                <tr key={f.id}>
                  <td>{f.original_name}</td>
                  <td style={{ color: 'var(--text2)', fontSize: '0.85rem' }}>{f.created_at?.slice(0, 16).replace('T', ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ====== АДМИН: ПРОСМОТР ОБУЧЕНИЯ СОТРУДНИКА ====== */}
      {isAdmin && (
        <div style={{ padding: 20, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
          <h3 style={{ marginBottom: 12 }}>Просмотр обучения сотрудника</h3>
          <select
            value={viewUserId}
            onChange={e => loadViewUser(e.target.value)}
            style={{ padding: '10px 14px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: '0.9rem', minWidth: 250, marginBottom: 16 }}
          >
            <option value="">— Выберите сотрудника —</option>
            {allUsers.map(u => (
              <option key={u.id} value={u.id}>{u.internal_name} ({u.display_login})</option>
            ))}
          </select>

          {viewUserData && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {viewUserData.role_description && (
                <div style={{ padding: 12, background: 'var(--bg3)', borderRadius: 6 }}>
                  <strong style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>Роль:</strong> {viewUserData.role_description}
                </div>
              )}
              <div style={{ padding: 12, background: 'var(--bg3)', borderRadius: 6 }}>
                <strong style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>Скилы:</strong>
                <pre style={{ whiteSpace: 'pre-wrap', marginTop: 4, fontSize: '0.85rem' }}>{viewUserData.skills_text || 'Не заполнено'}</pre>
              </div>
              <div>
                <strong style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>Файлы ({viewUserData.files?.length || 0}):</strong>
                {viewUserData.files?.length > 0 ? (
                  <div style={{ marginTop: 8 }}>
                    {viewUserData.files.map(f => (
                      <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                        <span>{f.original_name}</span>
                        <span style={{ color: 'var(--text2)' }}>{f.created_at?.slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                ) : <div style={{ color: 'var(--text2)', fontSize: '0.85rem', marginTop: 4 }}>Нет файлов</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
